import { Model } from 'app/model/backendmodel';
import { XmlDataset } from 'app/model/dataset/xml.dataset';
import { AdapterRegistry } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { compressionAdapts } from './file-type.dataset.adapter';
import { FormatDatasetAdapter } from 'app/model/adapters/datasets/format-dataset.adapter';
import { HierarchicalNode } from 'app/model/dataset/hierarchicalnode';
import { parseJsonSchema } from './json.dataset.adapter';
import { ValueAdapter } from '../value.adapter';
import _ from 'lodash';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(XmlDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            schema: '*',
            parameters: '*',
            type: '*',
            linkedServiceName: '*',
            linkedService: '*',
            connectionSettings: __IS_FABRIC_DPE__ ? '*' : undefined,
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                location: '*',
                compression: compressionAdapts,
                encodingName: '*',
                nullValue: '*'
            }
        }
    }
})
export class XmlDatasetAdapter extends FormatDatasetAdapter<XmlDataset, Model.DatasetV2Resource> {
    public parseHierarchicalSchema(obj: unknown): HierarchicalNode {
        try {
            return parseJsonSchema(obj, 'schema');
        } catch {
            return undefined;
        }
    }

    protected async _toResource(dataset: XmlDataset): Promise<Model.DatasetV2Resource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetV2Resource;
        const modelDataset = this.properties(modelDatasetResource) as Model.XmlDataset;
        modelDataset.type = Metamodel.modelFor(XmlDataset).name;
        modelDataset.typeProperties = modelDataset.typeProperties || {};
        if (dataset.location) {
            modelDataset.typeProperties.location = this.locationToResource(dataset);
        }

        const compression = ValueAdapter.toResource(dataset, XmlDataset.COMPRESSION_CODEC);
        if (compression != null) {
            modelDataset.typeProperties.compression = {
                type: compression,
                level: ValueAdapter.toResource(dataset, XmlDataset.COMPRESSION_LEVEL)
            };
        }
        modelDataset.typeProperties.encodingName = ValueAdapter.toResource(dataset, XmlDataset.ENCODING_NAME);
        modelDataset.typeProperties.nullValue = ValueAdapter.toResource(dataset, XmlDataset.NULL_VALUE);

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: XmlDataset): Promise<XmlDataset> {
        if (!dataset) {
            dataset = XmlDataset.MODEL.create<XmlDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties
            && modelDatasetResource.properties.typeProperties.location) {
            await this.locationToModel(modelDatasetResource, dataset);
        }

        if (modelDatasetResource.properties.typeProperties != null) {
            if (modelDatasetResource.properties.typeProperties.compression != null) {
                ValueAdapter.toModel(dataset, XmlDataset.COMPRESSION_CODEC, modelDatasetResource.properties.typeProperties.compression.type);
                ValueAdapter.toModel(dataset, XmlDataset.COMPRESSION_LEVEL, modelDatasetResource.properties.typeProperties.compression.level);
            }
            ValueAdapter.toModel(dataset, XmlDataset.ENCODING_NAME, modelDatasetResource.properties.typeProperties.encodingName);
            ValueAdapter.toModel(dataset, XmlDataset.NULL_VALUE, modelDatasetResource.properties.typeProperties.nullValue);
        }

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, XmlDataset.MODEL));
        return dataset;
    }
}
